import { Alert, Button, Form, Input } from "antd"
import { useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { LockOutlined } from "@ant-design/icons"
import AuthShell from "../../Layout/AuthShell"
import apiClient from "../../Utils/apiClient"
import generateStrongPassword from "../../Utils/generateStrongPassword"

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const token = useMemo(() => searchParams.get("token") || "", [searchParams])
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const generatePassword = () => {
    const password = generateStrongPassword(12)
    form.setFieldsValue({
      password,
      passwordConfirm: password,
    })
  }

  const onFinish = async (values) => {
    setLoading(true)
    setError("")
    try {
      const res = await apiClient(
        "auth/reset-password",
        "POST",
        {
          token,
          password: values.password,
          passwordConfirm: values.passwordConfirm,
        },
        { refresh: false }
      )

      if (res.success) {
        navigate("/login", { replace: true, state: { passwordReset: true } })
      } else {
        setError(res.message || "This reset link is invalid or has expired.")
      }
    } catch (err) {
      setError("Could not reset the password. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <AuthShell
        title="Reset password"
        subtitle="This reset link is missing or incomplete."
        badge="Account recovery"
        steps={["Credentials", "Verify", "Console"]}
        activeStep={1}
      >
        <Alert
          type="error"
          showIcon
          message="Invalid reset link"
          description="Request a new password reset from the sign-in page."
        />
        <div className="text-center mt-3">
          <Link to="/forgot-password">Request a new link</Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="Use at least 8 characters. You’ll sign in with this password next time."
      footer="After reset you’ll return to sign-in. Two-factor verification is still required."
      badge="Account recovery"
      steps={["Credentials", "Verify", "Console"]}
      activeStep={1}
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          className="mb-3"
          closable
          onClose={() => setError("")}
        />
      ) : null}

      <Form
        form={form}
        className="auth-form"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
      >
        <Form.Item
          label="New password"
          name="password"
          rules={[
            { required: true, message: "Enter a new password" },
            { min: 8, message: "Minimum 8 characters" },
          ]}
          hasFeedback
        >
          <Input.Password
            size="large"
            prefix={<LockOutlined className="text-muted" />}
            placeholder="New password"
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item
          label="Confirm password"
          name="passwordConfirm"
          dependencies={["password"]}
          hasFeedback
          rules={[
            { required: true, message: "Confirm your new password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error("Passwords do not match"))
              },
            }),
          ]}
        >
          <Input.Password
            size="large"
            prefix={<LockOutlined className="text-muted" />}
            placeholder="Confirm password"
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item>
          <Button block onClick={generatePassword}>
            Generate a strong password
          </Button>
        </Form.Item>
        <Form.Item className="mb-0">
          <Button
            className="auth-submit"
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={loading}
          >
            Reset password
          </Button>
        </Form.Item>
      </Form>
      <div className="text-center mt-3">
        <Link to="/login">Back to sign-in</Link>
      </div>
    </AuthShell>
  )
}

export default ResetPassword
